import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, CreditCard, Banknote, Tag, X, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const { cart, getCartTotal, clearCart } = useCart();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('online'); // 'online', 'cod'
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: user?.name || '',
    customer_email: user?.email || '',
    customer_phone: '',
    street: '',
    city: '',
    state: '',
    pincode: ''
  });

  // Pre-fill form with user data
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        customer_name: user.name || prev.customer_name,
        customer_email: user.email || prev.customer_email
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setCouponLoading(true);
    try {
      const response = await axios.post(`${API}/coupons/validate`, {
        code: couponCode,
        cart_total: getCartTotal(),
        user_email: formData.customer_email || null
      });
      
      if (response.data.valid) {
        setCouponApplied(response.data);
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
        setCouponApplied(null);
      }
    } catch (error) {
      toast.error('Failed to validate coupon');
      setCouponApplied(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponApplied(null);
    setCouponCode('');
  };

  const getFinalTotal = () => {
    if (couponApplied && couponApplied.valid) {
      return couponApplied.final_amount;
    }
    return getCartTotal();
  };

  const getDiscountAmount = () => {
    if (couponApplied && couponApplied.valid) {
      return couponApplied.discount_amount;
    }
    return 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create order
      const orderData = {
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        shipping_address: {
          street: formData.street,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode
        },
        items: cart,
        subtotal: getCartTotal(),
        discount_amount: getDiscountAmount(),
        coupon_code: couponApplied?.coupon_code || null,
        total_amount: getFinalTotal(),
        payment_method: paymentMethod
      };

      const orderResponse = await axios.post(`${API}/orders`, orderData);
      const order = orderResponse.data;

      if (paymentMethod === 'cod') {
        // Process COD order
        const codResponse = await axios.post(`${API}/payment/cod`, {
          order_id: order.id
        });
        
        if (codResponse.data.success) {
          clearCart();
          navigate(`/order-success?order_id=${order.id}&payment=cod`);
        } else {
          toast.error(codResponse.data.message || 'Failed to place COD order');
          setLoading(false);
        }
      } else {
        // Online payment - Create checkout session (Stripe for now)
        // When Razorpay is configured, this will be updated
        const checkoutData = {
          order_id: order.id,
          origin_url: window.location.origin
        };

        const sessionResponse = await axios.post(`${API}/checkout/session`, checkoutData);
        
        // Redirect to payment gateway
        window.location.href = sessionResponse.data.url;
      }
    } catch (error) {
      console.error('Error during checkout:', error);
      toast.error(error.response?.data?.detail || 'Failed to process checkout. Please try again.');
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="section-spacing" data-testid="checkout-page">
      <div className="container-custom">
        <h1 className="text-4xl md:text-5xl font-heading font-bold mb-12 text-center" data-testid="checkout-heading">
          Checkout
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="checkout-form">
              {/* Customer Information */}
              <div className="bg-white rounded-2xl p-8 shadow-card">
                <h2 className="text-2xl font-heading font-bold mb-6">Customer Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Full Name *</label>
                    <Input
                      data-testid="customer-name-input"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email *</label>
                    <Input
                      data-testid="customer-email-input"
                      type="email"
                      name="customer_email"
                      value={formData.customer_email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2">Phone Number *</label>
                    <Input
                      data-testid="customer-phone-input"
                      type="tel"
                      name="customer_phone"
                      value={formData.customer_phone}
                      onChange={handleChange}
                      required
                      pattern="[0-9]{10}"
                      placeholder="10-digit mobile number"
                    />
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="bg-white rounded-2xl p-8 shadow-card">
                <h2 className="text-2xl font-heading font-bold mb-6">Shipping Address</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Street Address *</label>
                    <Input
                      data-testid="street-input"
                      name="street"
                      value={formData.street}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">City *</label>
                      <Input
                        data-testid="city-input"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">State *</label>
                      <Input
                        data-testid="state-input"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Pincode *</label>
                    <Input
                      data-testid="pincode-input"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleChange}
                      required
                      pattern="[0-9]{6}"
                      placeholder="6-digit pincode"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-white rounded-2xl p-8 shadow-card">
                <h2 className="text-2xl font-heading font-bold mb-6">Payment Method</h2>
                <div className="space-y-4">
                  {/* Online Payment Option */}
                  <label 
                    className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      paymentMethod === 'online' 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50'
                    }`}
                    data-testid="payment-online"
                  >
                    <input
                      type="radio"
                      name="payment_method"
                      value="online"
                      checked={paymentMethod === 'online'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded-full border-2 mr-4 flex items-center justify-center ${
                      paymentMethod === 'online' ? 'border-primary' : 'border-gray-300'
                    }`}>
                      {paymentMethod === 'online' && <div className="w-3 h-3 rounded-full bg-primary" />}
                    </div>
                    <CreditCard className="w-6 h-6 mr-3 text-primary" />
                    <div>
                      <p className="font-semibold">Pay Online</p>
                      <p className="text-sm text-muted-foreground">Credit/Debit Card, UPI, Net Banking</p>
                    </div>
                  </label>

                  {/* COD Option */}
                  <label 
                    className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      paymentMethod === 'cod' 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50'
                    }`}
                    data-testid="payment-cod"
                  >
                    <input
                      type="radio"
                      name="payment_method"
                      value="cod"
                      checked={paymentMethod === 'cod'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded-full border-2 mr-4 flex items-center justify-center ${
                      paymentMethod === 'cod' ? 'border-primary' : 'border-gray-300'
                    }`}>
                      {paymentMethod === 'cod' && <div className="w-3 h-3 rounded-full bg-primary" />}
                    </div>
                    <Banknote className="w-6 h-6 mr-3 text-primary" />
                    <div>
                      <p className="font-semibold">Cash on Delivery</p>
                      <p className="text-sm text-muted-foreground">Pay when you receive your order</p>
                    </div>
                  </label>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="btn-accent w-full"
                data-testid="place-order-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : paymentMethod === 'cod' ? (
                  'Place Order (COD)'
                ) : (
                  'Proceed to Payment'
                )}
              </Button>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-8 shadow-card sticky top-24" data-testid="checkout-summary">
              <h2 className="text-2xl font-heading font-bold mb-6">Order Summary</h2>
              
              <div className="space-y-4 mb-6">
                {cart.map((item) => (
                  <div key={`${item.product_id}-${item.variant_size}`} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {item.product_name} ({item.variant_size}) x{item.quantity}
                    </span>
                    <span className="font-semibold">₹{(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              {/* Coupon Code */}
              <div className="border-t border-border pt-4 mb-4">
                <label className="block text-sm font-medium mb-2">Have a coupon?</label>
                {couponApplied && couponApplied.valid ? (
                  <div className="flex items-center justify-between bg-green-50 p-3 rounded-lg">
                    <div className="flex items-center">
                      <Check className="w-5 h-5 text-green-600 mr-2" />
                      <span className="font-medium text-green-700">{couponApplied.coupon_code}</span>
                    </div>
                    <button 
                      type="button" 
                      onClick={handleRemoveCoupon}
                      className="text-gray-500 hover:text-gray-700"
                      data-testid="remove-coupon-btn"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      data-testid="coupon-input"
                      placeholder="Enter code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      className="flex-1"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={handleApplyCoupon}
                      disabled={couponLoading || !couponCode.trim()}
                      data-testid="apply-coupon-btn"
                    >
                      {couponLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />}
                    </Button>
                  </div>
                )}
              </div>

              <div className="border-t border-border pt-4 space-y-3">
                <div className="flex justify-between text-lg">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-semibold">₹{getCartTotal().toFixed(2)}</span>
                </div>
                
                {couponApplied && couponApplied.valid && (
                  <div className="flex justify-between text-lg text-green-600">
                    <span>Discount</span>
                    <span className="font-semibold">-₹{getDiscountAmount().toFixed(2)}</span>
                  </div>
                )}
                
                <div className="flex justify-between text-lg">
                  <span className="text-muted-foreground">Shipping</span>
                  <span className="font-semibold text-green-600">Free</span>
                </div>
                
                <div className="border-t border-border pt-3">
                  <div className="flex justify-between text-2xl font-bold">
                    <span>Total</span>
                    <span className="text-primary" data-testid="checkout-total">₹{getFinalTotal().toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Inclusive of all taxes</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
